import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  ListItemIcon,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import CustomPagination from "../../../../components/CustomPagination/CustomPagination";
import { useLanguages } from "../../../../context/LanguagesContext";
import { usePageMetadata } from "../../../../context/PageMetadataContext";
import { usePortal } from "../../../../context/PortalsContext";
import { useToast } from "../../../../context/ToastContext";
import { useAuth } from "../../../../context/AuthContext";
import {
  findOrTranslate,
  replaceTranslation,
} from "../../../../services/api/apiClient";
import {
  findOrReplaceParams,
  ModeType,
  PortalLanguageTranslation,
  ReplaceTranslationBodyParams,
  TranslationPaginatedResult,
  TranslationResult,
} from "../../../../services/api/types/portalConfiguration/translations";
import { brand, error, gray, warning } from "../../../../theme/themePrimitives";
import { fromCamelCase } from "../../../../utils/helpers";
import { MAX_LENGTH_DESCRIPTION } from "../../../../utils/inputLimits";
import { ReplaceModal } from "./components/ReplaceModal";

interface FindAndReplaceProps {}

export const FindAndReplace: React.FC<FindAndReplaceProps> = () => {
  const { showToast } = useToast();
  const [replaceLoading, setReplaceLoading] = useState(false);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [replaceQuery, setReplaceQuery] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [attributesError, setAttributesError] = useState("");
  const [selectedAttributes, setSelectedAttributes] = useState<
    TranslationResult[]
  >([]);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [translationsResponse, setTranslationsResponse] =
    useState<TranslationPaginatedResult>();
  const [translationsArray, setTranslationsArray] =
    useState<TranslationResult[]>();
  const [translationsChips, setTranslationsChips] = useState<
    PortalLanguageTranslation[]
  >([]);
  const [selectedWord, setSelectedWord] = useState<PortalLanguageTranslation>();
  const {
    languages,
    dispatch: languagesDispatch,
    getLanguageDetails,
  } = useLanguages();
  const { portals } = usePortal();
  const { auth } = useAuth();
  const { portalUid } = useParams<{ portalUid: string }>();

  const breadcrumbs = [
    {
      label: "Portal configuration",
      path: `/portal-configuration`,
    },
    {
      label: "Portal translation",
      path: `/portal-configuration/${portalUid}/portal-translation`,
    },
    {
      label: "Find & replace",
    },
  ];

  const setSelectedLanguage = (e: string) => {
    languagesDispatch({ type: "SET_SELECTED_LANGUAGE", payload: e });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleReplaceQueryChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTranslationsChips([]);
    setReplaceQuery(event.target.value);
  };

  const handleRowsChange = (e: any) => {
    setLimit(e.target.value as number);
  };

  const handleChipClick = (word: PortalLanguageTranslation) => {
    setSelectedWord(word);
    setOpenModal(true);
  };

  const handleCheckboxChange = (translation: TranslationResult) => {
    setSelectedAttributes((prev) => {
      const exists = prev.some(
        (t) =>
          t.attributeValue === translation.attributeValue &&
          t.tabId === translation.tabId &&
          t.tabSectionKey === translation.tabSectionKey,
      );
      if (exists) {
        return prev.filter(
          (t) =>
            !(
              t.attributeValue === translation.attributeValue &&
              t.tabId === translation.tabId &&
              t.tabSectionKey === translation.tabSectionKey
            ),
        );
      } else {
        return [...prev, translation];
      }
    });
    setAttributesError("");
  };

  const isChecked = (translation: TranslationResult) =>
    selectedAttributes.some(
      (t) =>
        t.attributeValue === translation.attributeValue &&
        t.tabId === translation.tabId &&
        t.tabSectionKey === translation.tabSectionKey,
    );

  const handleSelectedWordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const updatedWord: PortalLanguageTranslation = {
      languageCode: selectedWord?.languageCode!,
      translation: event.target.value,
    };
    setSelectedWord(updatedWord);
  };

  const changeTranslation = () => {
    setTranslationsChips((prev) =>
      prev.map((translation) => {
        if (translation.languageCode === selectedWord?.languageCode) {
          return {
            ...translation,
            translation: selectedWord.translation,
          };
        }
        return translation;
      }),
    );
    setOpenModal(false);
  };

  const handleReplace = async () => {
    try {
      setReplaceLoading(true);
      if (portals.selectedPortal && auth.user?.UserPortalUid) {
        if (!selectedAttributes.length && !selectAll) {
          setAttributesError("You must select at least one attribute.");
          return;
        }
        const body: ReplaceTranslationBodyParams = {
          wordFind: searchQuery,
          wordReplace: replaceQuery,
          adminUser: auth.user?.UserPortalUid,
          translations: translationsChips,
          attributes: selectAll ? translationsArray! : selectedAttributes,
        };
        await replaceTranslation(
          portals.selectedPortal.portalUid,
          languages.selectedLanguage,
          body,
        );
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error?.response?.data || "Something went wrong", "error");
    } finally {
      setReplaceLoading(false);
    }
  };

  const handlePreviousClick = () => {
    if (translationsResponse?.previousPage) {
      const params = new URLSearchParams(translationsResponse.previousPage);
      const limit = parseInt(params.get("limit")!);
      const offset = parseInt(params.get("offset")!);
      handleSearchOrTranslate({
        word: searchQuery,
        mode: ModeType.FIND,
        limit,
        offset,
      });
      setPage((prev) => prev - 1);
    }
  };

  const handleNextClick = () => {
    if (translationsResponse?.nextPage) {
      const params = new URLSearchParams(translationsResponse.nextPage);
      const limit = parseInt(params.get("limit")!);
      const offset = parseInt(params.get("offset")!);
      handleSearchOrTranslate({
        word: searchQuery,
        mode: ModeType.FIND,
        limit,
        offset,
      });
      setPage((prev) => prev + 1);
    }
  };

  const handlePageNumberClick = (pageNumber: number) => {
    handleSearchOrTranslate({
      word: searchQuery,
      mode: ModeType.FIND,
      limit: limit,
      offset: (pageNumber - 1) * limit,
    });
    setPage(pageNumber);
  };

  const handleSearchOrTranslate = async (
    params: findOrReplaceParams = { word: searchQuery, mode: ModeType.FIND },
  ) => {
    try {
      if (portals.selectedPortal) {
        if (params.mode === ModeType.FIND) {
          setSearchLoading(true);
          const data: TranslationPaginatedResult = await findOrTranslate(
            portals.selectedPortal.portalUid,
            languages.selectedLanguage,
            params,
          );
          setTranslationsResponse(data);
          setTranslationsArray(data.rows);
        } else {
          setTranslateLoading(true);
          const data: PortalLanguageTranslation[] = await findOrTranslate(
            portals.selectedPortal.portalUid,
            languages.selectedLanguage,
            params,
          );
          setTranslationsChips(data);
        }
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error?.response?.data || "Something went wrong", "error");
    } finally {
      setSearchLoading(false);
      setTranslateLoading(false);
    }
  };

  usePageMetadata(
    {
      title: "Find & Replace",
      breadcrumbs: breadcrumbs,
    },
    [portalUid],
  );

  return (
    <>
      <Paper elevation={1} sx={{ border: "1px solid #EAECF0" }}>
        <Box p={2}>
          <Box
            sx={{
              display: "flex",
              gap: "1rem",
              mb: 1,
              alignItems: "center",
            }}
          >
            <Select
              id="find-and-replace-language-select"
              IconComponent={ExpandMoreIcon}
              value={languages.selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              sx={{
                borderRadius: "12px",
                minWidth: "150px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "5px 14px",
                },
              }}
              size="small"
            >
              {languages.languages.map((lang, index) => (
                <MenuItem
                  key={index}
                  value={lang.langugageCode}
                  sx={{
                    my: 0.5,
                    "&:hover": {
                      opacity: 0.9,
                    },
                  }}
                >
                  <ListItemIcon>
                    <img
                      src={lang.flag}
                      alt={lang.name}
                      style={{
                        border: `1px solid ${gray[300]}`,
                        borderRadius: "999px",
                        objectFit: "cover",
                        marginRight: "8px",
                      }}
                      height={24}
                      width={24}
                    />
                  </ListItemIcon>
                  <Typography>{lang.name}</Typography>
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              placeholder="Search"
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              slotProps={{
                input: {
                  sx: {
                    borderRadius: "12px",
                    backgroundColor: `${gray[100]} !important`,
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              onClick={() =>
                handleSearchOrTranslate({
                  word: searchQuery,
                  mode: ModeType.FIND,
                  limit,
                })
              }
              sx={{
                padding: "10px 16px",
                borderRadius: "12px",
                backgroundColor: `${brand[600]} !important`,
                color: `white`,
              }}
              disabled={!searchQuery}
            >
              Search
            </Button>
          </Box>
        </Box>

        <TableContainer sx={{ mt: 2 }}>
          <Table
            sx={{
              borderCollapse: "separate",
              borderSpacing: 0,
              width: "100%",
              tableLayout: "fixed",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <TableCell
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  Word or phrase
                </TableCell>
                <TableCell
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  Page
                </TableCell>
                <TableCell
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  Section
                </TableCell>
                <TableCell
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      id="find-and-replace-select-all-checkbox"
                      onChange={(e) => {
                        setAttributesError("");
                        setSelectAll(e.target.checked);
                      }}
                    />
                    Select all
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            {searchLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ height: 300 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <CircularProgress size={80} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <>
                {translationsResponse && translationsArray && (
                  <TableBody>
                    {translationsArray.map((translation, index) => {
                      return (
                        <React.Fragment key={index}>
                          <TableRow
                            sx={{
                              backgroundColor:
                                index % 2 !== 0 ? "white" : gray[100],
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <TableCell>{translation.attributeValue}</TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  width: "fit-content",
                                  padding: "2px 8px",
                                  borderRadius: "999px",
                                  border: "1px solid #B2DDFF",
                                  color: "#175CD3",
                                  backgroundColor: "#EFF8FF",
                                }}
                              >
                                {fromCamelCase(translation.tabId)}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  width: "fit-content",
                                  padding: "2px 8px",
                                  borderRadius: "999px",
                                  border: `1px solid ${warning[200]}`,
                                  color: warning[700],
                                  backgroundColor: warning[50],
                                }}
                              >
                                {fromCamelCase(translation.tabSectionKey)}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                checked={isChecked(translation)}
                                onChange={() =>
                                  handleCheckboxChange(translation)
                                }
                              />
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                )}
              </>
            )}
          </Table>
        </TableContainer>
        {translationsResponse && (
          <CustomPagination
            rows={
              translationsResponse.count > 4
                ? limit
                : translationsResponse.count
            }
            itemsCount={translationsResponse.count}
            handleRowsChange={handleRowsChange}
            totalPages={Math.ceil(translationsResponse.count / limit)}
            currentPage={page}
            onPageNumberClick={handlePageNumberClick}
            onNextClick={handleNextClick}
            onPreviousClick={handlePreviousClick}
          />
        )}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            borderTop: "1px solid #EAECF0",
            p: 2,
            zIndex: 10,
          }}
        >
          {attributesError && (
            <Box sx={{ display: "flex", justifyContent: "center" }} my={2}>
              <Typography color={error[600]}>{attributesError}</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Replace a word or a phrase"
              variant="outlined"
              fullWidth
              value={replaceQuery}
              onBlur={() => {
                if (replaceQuery) {
                  handleSearchOrTranslate({
                    word: replaceQuery,
                    mode: ModeType.TRANSLATE,
                  });
                }
              }}
              onChange={handleReplaceQueryChange}
              slotProps={{
                htmlInput: { maxLength: MAX_LENGTH_DESCRIPTION },
              }}
            />
            {replaceLoading ? (
              <CircularProgress />
            ) : (
              <Button
                sx={{
                  padding: "10px 16px",
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                }}
                disabled={!replaceQuery}
                onClick={handleReplace}
              >
                Replace
              </Button>
            )}
          </Box>
          {translateLoading ? (
            <Box mt={1}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {translationsChips && (
                <Box mt={1} sx={{ display: "flex", gap: "0.5rem" }}>
                  {translationsChips.map((translation, index) => {
                    return (
                      <Chip
                        onClick={() => handleChipClick(translation)}
                        key={index}
                        label={translation.translation}
                        deleteIcon={<EditIcon />}
                        onDelete={() => {}}
                        avatar={
                          <img
                            src={
                              getLanguageDetails(translation.languageCode)?.flag
                            }
                            alt={translation.translation}
                            width={30}
                            height={30}
                            style={{
                              border: `1px solid ${gray[300]}`,
                              borderRadius: "999px",
                              objectFit: "cover",
                            }}
                          />
                        }
                        sx={{
                          backgroundColor: "white",
                          padding: "14px 6px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
      {selectedWord && (
        <ReplaceModal
          word={selectedWord}
          changeTranslation={changeTranslation}
          openModal={openModal}
          setOpenModal={setOpenModal}
          handleSelectedWordChange={handleSelectedWordChange}
        />
      )}
    </>
  );
};
